import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Loader2, Sparkles, Heart } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("demo@survivingtogether.org");
    setPassword("SurvivingDemo2026");
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex flex-col lg:flex-row">
      {/* Hero image side */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBjb21tdW5pdHklMjBoZWxwaW5nfGVufDB8fHx8MTc4NzE4NDUyMnww&ixlib=rb-4.1.0&q=85"
          alt="Community"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1B2A4A]/70 via-[#1B2A4A]/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <p className="font-heading font-bold text-2xl">Surviving Together</p>
          </div>
          <h1 className="font-heading font-bold text-4xl leading-tight mb-3">
            Community helping community thrive.
          </h1>
          <p className="text-white/85 text-base max-w-md">
            Share cost-saving tips, track your budget, and take a moment to breathe — all with neighbors who care.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-lg text-[#1B2A4A]">Surviving Together</p>
              <p className="text-xs text-[#1B2A4A]/60">Thriving Together</p>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold text-[#1B2A4A] mb-2">Welcome back</h2>
          <p className="text-[#1B2A4A]/60 mb-8">Sign in to see what your community is saving today.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1B2A4A] font-semibold text-sm">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1B2A4A] font-semibold text-sm">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white font-semibold text-base transition-colors duration-200"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
            </Button>

            <button
              type="button"
              data-testid="login-fill-demo"
              onClick={fillDemo}
              className="w-full text-sm text-[#2A9D8F] hover:text-[#218276] font-semibold transition-colors duration-200"
            >
              <Heart className="w-3.5 h-3.5 inline mr-1" /> Use demo account
            </button>

            <p className="text-center text-sm text-[#1B2A4A]/70">
              New here?{" "}
              <Link data-testid="link-register" to="/register" className="text-[#E8621A] font-semibold hover:underline">
                Create an account
              </Link>
            </p>

            <p className="text-center text-xs text-[#1B2A4A]/50 pt-2">
              <Link to="/privacy" data-testid="login-footer-privacy" className="hover:text-[#E8621A] font-semibold">
                Privacy Policy
              </Link>
              <span className="mx-2">·</span>
              <Link to="/terms" data-testid="login-footer-terms" className="hover:text-[#E8621A] font-semibold">
                Terms of Service
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
